import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { dropdownSpec, fieldSpec, type FieldSize } from "../specs/controls";
import { mergeVars, typographyClass } from "../specs/types";
import { ChevronDownIcon, CloseIcon, SearchIcon } from "./Icons";

const fieldVars = (size: FieldSize) => mergeVars(fieldSpec.base, fieldSpec.sizes[size].vars);

/* ------------------------------------------------------------- FieldShell */

export interface FieldShellProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor?: string;
  size?: FieldSize;
  children: ReactNode;
  className?: string;
}

/** Обвязка любого поля: подпись, подсказка, ошибка. */
export function FieldShell({
  label,
  hint,
  error,
  required,
  htmlFor,
  size = fieldSpec.defaults.size,
  children,
  className,
}: FieldShellProps) {
  return (
    <div
      className={["ui-field", className].filter(Boolean).join(" ")}
      style={fieldVars(size) as CSSProperties}
    >
      {label && (
        <label className={`ui-field__label ${typographyClass(fieldSpec.labelTypography)}`} htmlFor={htmlFor}>
          {label}
          {required && <span className="ui-field__required">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <span className={`ui-field__error ${typographyClass(fieldSpec.hintTypography)}`}>{error}</span>
      ) : (
        hint && <span className={`ui-field__hint ${typographyClass(fieldSpec.hintTypography)}`}>{hint}</span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Input */

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  size?: FieldSize;
  invalid?: boolean;
}

export function Input({ size = fieldSpec.defaults.size, invalid, className, style, ...rest }: InputProps) {
  return (
    <input
      {...rest}
      data-invalid={invalid}
      className={["ui-input", typographyClass(fieldSpec.sizes[size].typography), className]
        .filter(Boolean)
        .join(" ")}
      style={{ ...(fieldVars(size) as CSSProperties), ...style }}
    />
  );
}

/* ----------------------------------------------------------------- Search */

export interface SearchProps extends InputProps {
  /** Вызывается кнопкой очистки. Без него крестик не показывается. */
  onClear?: () => void;
  clearLabel?: string;
}

export function Search({
  size = "large",
  className,
  style,
  onClear,
  clearLabel = "Clear",
  ...rest
}: SearchProps) {
  const clearable = Boolean(onClear) && Boolean(rest.value);
  return (
    <div
      className="ui-input-group"
      data-clearable={clearable}
      style={fieldVars(size) as CSSProperties}
    >
      <SearchIcon className="ui-input-group__icon" />
      <input
        {...rest}
        type="search"
        className={["ui-input", typographyClass(fieldSpec.sizes[size].typography), className]
          .filter(Boolean)
          .join(" ")}
        style={style}
      />
      {clearable && (
        <button type="button" className="ui-input-group__clear" onClick={onClear} aria-label={clearLabel}>
          <CloseIcon />
        </button>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- Textarea */

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  size?: FieldSize;
  invalid?: boolean;
}

export function Textarea({ size = fieldSpec.defaults.size, invalid, className, style, ...rest }: TextareaProps) {
  return (
    <textarea
      {...rest}
      data-invalid={invalid}
      className={["ui-textarea", typographyClass(fieldSpec.sizes[size].typography), className]
        .filter(Boolean)
        .join(" ")}
      style={{ ...(fieldVars(size) as CSSProperties), ...style }}
    />
  );
}

/* ----------------------------------------------------------------- Select */

export interface SelectOption<T = string> {
  label: string;
  value: T;
  description?: string;
}

export interface SelectProps<T = string> {
  value: T | null;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  size?: FieldSize;
  invalid?: boolean;
  disabled?: boolean;
  id?: string;
}

export function Select<T extends string | number | boolean>({
  value,
  options,
  onChange,
  placeholder = "—",
  size = fieldSpec.defaults.size,
  invalid,
  disabled,
  id,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const generatedId = useId();
  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    const onDocumentClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  const vars = mergeVars(fieldVars(size), dropdownSpec.vars);

  return (
    <div className="ui-select" ref={rootRef} style={vars as CSSProperties}>
      <button
        type="button"
        id={id ?? generatedId}
        disabled={disabled}
        data-open={open}
        data-invalid={invalid}
        onClick={() => setOpen((current) => !current)}
        className={`ui-select__trigger ${typographyClass(fieldSpec.sizes[size].typography)}`}
      >
        <span className="ui-select__value" data-placeholder={!selected}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDownIcon className="ui-select__chevron" />
      </button>

      {open && (
        <div className="ui-dropdown" role="listbox">
          {options.map((option) => (
            <button
              key={String(option.value)}
              type="button"
              role="option"
              aria-selected={option.value === value}
              data-selected={option.value === value}
              className={`ui-dropdown__item ${typographyClass(dropdownSpec.itemTypography)}`}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
